// src/public/tests/LogicalProcessExamples.js
import { useState } from 'react';
import {
    Container, Paper, Typography, Button, Box, Alert, Divider, 
    LinearProgress, FormControl, Select, MenuItem, Stack
} from '@mui/material';
import { CheckCircle, Cancel, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const example = {
    id: 'E1',
    statement: [
        'X es de diferente sexo que Y;',
        'Si Z es masculino, Y es femenino y viceversa;',
        'X es femenino.'
    ],
    subQuestions: [
        { 
            question: '¿Cuál es el sexo de Z?', 
            options: ['Masculino', 'Femenino'], 
            correctAnswer: 1 
        },
        { 
            question: '¿Cuál es el sexo de Y?', 
            options: ['Masculino', 'Femenino'], 
            correctAnswer: 0 
        }
    ]
};

const LogicalProcessExamples = ({ onStartTest, loading }) => {
    const { t } = useTranslation();
    const [subAnswers, setSubAnswers] = useState({});
    const [feedback, setFeedback] = useState({});

    const handleSubAnswerChange = (subIndex, value) => {
        const newSubAnswers = {
            ...subAnswers,
            [subIndex]: value
        };
        setSubAnswers(newSubAnswers);

        // Verificar respuesta inmediatamente
        const subQuestion = example.subQuestions[subIndex];
        const isCorrect = value === subQuestion.correctAnswer;
        
        setFeedback(prev => ({
            ...prev,
            [subIndex]: {
                isCorrect,
                correctAnswer: subQuestion.correctAnswer
            }
        }));
    };

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    {t('reasoningTests.practiceExamples')}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    {t('reasoningTests.practiceExamplesDescription')}
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('reasoningTests.example')} 1 {t('reasoningTests.of')} 1
                    </Typography>
                    <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                        {t('reasoningTests.itIsKnownThat')}
                    </Typography>

                    <Box sx={{ pl: 2 }}>
                        {example.statement.map((line, idx) => (
                            <Typography
                                key={idx}
                                variant="body1"
                                sx={{ mb: 1 }}
                            >
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {t('reasoningTests.questions')}:
                    </Typography>

                    <Stack spacing={3} sx={{ mt: 3 }}>
                        {example.subQuestions.map((subQ, idx) => (
                            <Box key={idx}>
                                <Typography variant="body1" gutterBottom sx={{ mb: 1.5 }}>
                                    {subQ.question}
                                </Typography>

                                <FormControl fullWidth size="small">
                                    <Select
                                        value={subAnswers[idx] ?? ''}
                                        onChange={(e) => handleSubAnswerChange(idx, e.target.value)}
                                        displayEmpty
                                        disabled={feedback[idx]?.isCorrect}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                color: subAnswers[idx] !== undefined ? 'text.primary' : 'text.secondary'
                                            }
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            {t('reasoningTests.selectAnOption')}
                                        </MenuItem>
                                        {subQ.options.map((option, optIdx) => (
                                            <MenuItem key={optIdx} value={optIdx}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {feedback[idx] && (
                                    <Alert 
                                        severity={feedback[idx].isCorrect ? 'success' : 'error'}
                                        icon={feedback[idx].isCorrect ? <CheckCircle /> : <Cancel />}
                                        sx={{ mt: 2 }}
                                    >
                                        {feedback[idx].isCorrect ? (
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                {t('reasoningTests.correct')}
                                            </Typography>
                                        ) : (
                                            <>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    {t('reasoningTests.incorrect')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {t('reasoningTests.correctAnswerIs')}: {subQ.options[subQ.correctAnswer]}
                                                </Typography>
                                            </>
                                        )}
                                    </Alert>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('reasoningTests.readyToStart')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrow />}
                        onClick={onStartTest}
                        disabled={loading}
                        sx={{ minWidth: '200px' }}
                    >
                        {t('reasoningTests.startTest')}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default LogicalProcessExamples;